import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../apiServices/axios';
import { INTELLIGENCE_INSIGHTS_V2_MODULE } from '../apiServices/api_routes';
import { modulePayloadToViewModel } from '../utils/intelligenceInsightsModuleViewModels';
import { insightsContextKey, withInsightsContextQuery } from '../utils/intelligenceInsightsContext';

export function useIntelligenceInsightsModule(projectId, moduleKey, { enabled = true, analysisContext = null, analysisId = null } = {}) {
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const payloadRef = useRef(null);
    const cacheRef = useRef({});
    payloadRef.current = payload;

    const contextKey = insightsContextKey(analysisContext, analysisId);

    // Restore cached payload for the target module to avoid empty flash on tab switch.
    useEffect(() => {
        const cached = cacheRef.current[`${contextKey}:${moduleKey}`] || null;
        setPayload(cached);
        payloadRef.current = cached;
        setError(null);
    }, [moduleKey, contextKey]);

    const fetchModule = useCallback(async () => {
        if (!projectId || !moduleKey) return;
        if (payloadRef.current == null) setLoading(true);
        setError(null);
        try {
            const { data } = await axiosInstance.get(
                withInsightsContextQuery(
                    INTELLIGENCE_INSIGHTS_V2_MODULE(projectId, moduleKey),
                    analysisContext,
                    { analysisId },
                ),
            );
            const next = data || null;
            cacheRef.current[`${contextKey}:${moduleKey}`] = next;
            setPayload(next);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not load insight module.');
            setPayload(null);
            cacheRef.current[`${contextKey}:${moduleKey}`] = null;
        } finally {
            setLoading(false);
        }
    }, [projectId, moduleKey, contextKey, analysisContext, analysisId]);

    useEffect(() => {
        if (enabled) void fetchModule();
    }, [enabled, fetchModule]);

    const resolvedPayload = useMemo(() => {
        const payloadKey = payload?.screen_key || payload?.module_key;
        if (payloadKey && moduleKey && payloadKey !== moduleKey) {
            return cacheRef.current[`${contextKey}:${moduleKey}`] || null;
        }
        return payload;
    }, [payload, moduleKey, contextKey]);

    const moduleData = useMemo(
        () => modulePayloadToViewModel(moduleKey, resolvedPayload),
        [moduleKey, resolvedPayload],
    );

    return {
        moduleData,
        payload,
        loading,
        error,
        fetchModule,
    };
}

export default useIntelligenceInsightsModule;
